import React, { Fragment } from 'react'
import Head from 'next/head'
import Container from '@dashboard/components/Container'
import ComparisonNav from '@dashboard/components/ComparisonNav'
import Flex from '@dashboard/components/Flex'
import Button from '@dashboard/components/Button'
import { Profile, LoadingStates, APIPagedResponse } from '@dashboard/lib/types'
import LoadingBox from '@dashboard/components/LoadingBox'
import ComparisonCard from '@dashboard/components/ComparisonCard'
import {
  PublicCelebQueryOptions,
  publicCelebritiesKey,
} from '@dashboard/store/actions/celebrities/public'
import ShareActions from '@dashboard/components/ShareActions'
import CelebrityPill from '@dashboard/components/CelebrityPill'
import { APP_URL } from '@dashboard/lib/constants'

import css from './styles.module.scss'

export interface StateProps {
  getCelebrityStates: Record<string, LoadingStates | undefined>
  getCelebrityResults: Record<string, APIPagedResponse<Profile> | undefined>
}

export interface DispatchProps {
  loadCelebrities: (options: PublicCelebQueryOptions) => void
}

export interface OwnProps {
  headerText: string
  subText: string
  ogTitle: string
  ogDescription: string
  hideShareActions?: boolean
}

type Props = StateProps & DispatchProps & OwnProps

interface State {
  searchValue: string
  query: string
  page: number
}

export default class FamousPeopleByPersonalityTypePage extends React.Component<
  Props,
  State
> {
  timeout?: number

  constructor(props: Props) {
    super(props)

    this.state = {
      searchValue: '',
      query: '',
      page: 1,
    }
  }

  componentDidMount() {
    const { loadCelebrities } = this.props
    const { query, page } = this.state

    loadCelebrities({ query, page })
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    const { searchValue, page } = this.state
    const updatedSearchValue = prevState.searchValue !== searchValue
    const updatedPage = prevState.page !== page

    if (updatedSearchValue) {
      if (this.timeout) clearTimeout(this.timeout)

      this.timeout = window.setTimeout(() => {
        this.setState({ query: this.state.searchValue, page: 1 }, () =>
          this.queryCelebs(),
        )
      }, 350)
    }

    if (updatedPage) this.queryCelebs()
  }

  render() {
    return (
      <div className={css.container}>
        <Head>{this.renderOGTags()}</Head>
        <div className={css.background} />
        {this.renderContent()}
      </div>
    )
  }

  // Partials
  renderOGTags() {
    const { ogTitle, ogDescription } = this.props

    return (
      <Fragment>
        <title>{ogTitle}</title>
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta name="description" content={ogDescription}></meta>
        <meta
          property="og:image"
          content={`${APP_URL}/static/share/images/famous_people.jpg`}
        />
        <meta property="og:image:width" content="1334" />
        <meta property="og:image:height" content="700" />
      </Fragment>
    )
  }

  renderContent() {
    const { headerText, subText } = this.props

    return (
      <Container>
        <ComparisonNav showLogin />
        <h1 className={css.pageTitle}>{headerText}</h1>
        <div className={css.divider} />
        <p className={css.subHeader}>{subText}</p>
        {this.renderCelebritiesCard()}
      </Container>
    )
  }

  renderBodyFailed() {
    return (
      <Flex vertical alignCenter>
        <p className={css.text}>There was a problem loading this page.</p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </Flex>
    )
  }

  renderCelebritiesCard() {
    return (
      <div className={css.celebritiesCardContainer}>
        <ComparisonCard noMargin>
          {this.renderSearchInput()}
          <Flex responsiveVertical>
            {this.renderShareActions()}
            {this.renderCelebritiesContent()}
          </Flex>
          {this.renderLoadingIndicator()}
          <Flex justifyCenter>{this.renderLoadMoreButton()}</Flex>
        </ComparisonCard>
      </div>
    )
  }

  renderLoadingIndicator() {
    const isLoading = ['default', 'loading'].includes(this.celebritiesState())

    if (!isLoading) return null

    return <LoadingBox />
  }

  renderLoadMoreButton() {
    const { page } = this.state

    if (
      this.celebritiesTotalPages() === page ||
      this.celebritiesState() === 'loading'
    )
      return null

    return (
      <div className={css.loadMoreButtonContainer}>
        <Button outline onClick={this.loadMore}>
          Load More
        </Button>
      </div>
    )
  }

  renderSearchInput() {
    const { searchValue } = this.state

    return (
      <Flex justifyCenter>
        <div className={css.inputContainer}>
          <input
            className={css.searchInput}
            type="text"
            value={searchValue}
            onChange={this.handleSearchUpdate}
            placeholder="Search for famous people"
          />
          <svg
            id="Email"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 36 36"
            className={css.inputIcon}
          >
            <title>Search</title>
            <polygon
              className={css.svgCls1}
              points="19.53 22.13 19.53 21.13 18.16 19.74 19.74 18.16 21.13 19.53 22.13 19.53 27.91 25.33 25.33 27.91 19.53 22.13"
            />
            <path
              className={css.svgCls1}
              d="M14.59,10.09a4.5,4.5,0,1,1-4.5,4.5,4.51,4.51,0,0,1,4.5-4.5m0-2a6.5,6.5,0,1,0,6.5,6.5,6.5,6.5,0,0,0-6.5-6.5Z"
            />
          </svg>
        </div>
      </Flex>
    )
  }

  renderCelebritiesContent() {
    const celebrities = this.celebritiesResult()
    const isLoading = ['default', 'loading'].includes(this.celebritiesState())

    if (this.celebritiesState() === 'failure') return this.renderBodyFailed()
    else if (!isLoading && celebrities.length === 0)
      return this.renderEmptyState()
    else
      return (
        <div className={css.celebrities}>
          {celebrities.map(this.renderCelebrity)}
        </div>
      )
  }

  renderEmptyState() {
    const { query } = this.state

    return (
      <div className={css.celebrities}>
        <p className={css.text}>{`No famous people found for "${query}"`}</p>
      </div>
    )
  }

  renderCelebrity = (celeb: Profile) => {
    return (
      <div className={css.celebPillContainer} key={celeb.id}>
        <CelebrityPill celebrity={celeb} />
      </div>
    )
  }

  renderShareActions() {
    const { hideShareActions } = this.props

    if (hideShareActions) return null

    return (
      <div className={css.shareActionsContainer}>
        <ShareActions
          responsiveHorizontal
          tweet={{
            text: 'Explore famous people’s personalities on Crystal <url> via @crystalknowsme',
          }}
        />
      </div>
    )
  }

  // Functions
  celebritiesResult() {
    const { getCelebrityResults } = this.props
    const { page, query } = this.state
    const celebs: Profile[] = []

    for (let i = 1; i <= page; i++) {
      const key = publicCelebritiesKey({ page: i, query })
      const data = getCelebrityResults[key]
      const profiles = data ? data.data : []

      celebs.push(...profiles)
    }

    return celebs
  }

  celebritiesKey() {
    const { page, query } = this.state

    return publicCelebritiesKey({ page, query })
  }

  celebritiesTotalPages() {
    const { getCelebrityResults } = this.props
    const results = getCelebrityResults[this.celebritiesKey()]

    return results?.total_pages ?? 1
  }

  celebritiesTotalCount() {
    const { getCelebrityResults } = this.props
    const results = getCelebrityResults[this.celebritiesKey()]

    return results?.total_count || 0
  }

  celebritiesState() {
    const { getCelebrityStates } = this.props

    return getCelebrityStates[this.celebritiesKey()] || 'default'
  }

  handleSearchUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchValue: e.target.value, page: 1 })
  }

  queryCelebs() {
    const { loadCelebrities } = this.props
    const { query, page } = this.state

    if (this.celebritiesState() === 'default') loadCelebrities({ query, page })
  }

  loadMore = () => {
    const { page } = this.state

    this.setState({ page: page + 1 })
  }
}
